import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import hillCountryRivers from "@/assets/hill-country-rivers.jpg";
import sanAntonioRiverwalk from "@/assets/san-antonio-riverwalk.jpg";
import newBraunfels from "@/assets/new-braunfels.webp";
import boerne from "@/assets/boerne.jpg";
import canyonLake from "@/assets/canyon-lake.jpg";
import fairOaksRanch from "@/assets/fair-oaks-ranch.jpg";
import lakehills from "@/assets/lakehills.jpg";
import seguin from "@/assets/seguin.jpg";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";

const ServiceAreas = () => {
  const areas = [{
    name: "San Antonio",
    description: "Our home base, serving all neighborhoods and districts in San Antonio with premium black car service.",
    path: "/san-antonio-car-service"
  }, {
    name: "New Braunfels",
    description: "Professional chauffeur service connecting New Braunfels to San Antonio and surrounding areas.",
    path: "/new-braunfels-car-service"
  }, {
    name: "Boerne",
    description: "Luxury transportation for Boerne residents and visitors to any destination.",
    path: "/boerne-car-service"
  }, {
    name: "Canyon Lake",
    description: "Reliable black car service for Canyon Lake area residents and vacation rentals.",
    path: "/canyon-lake-car-service"
  }, {
    name: "Fair Oaks Ranch",
    description: "Executive transportation for Fair Oaks Ranch community and beyond.",
    path: "/fair-oaks-ranch-car-service"
  }, {
    name: "Lakehills",
    description: "Connecting Lakehills to San Antonio and other destinations with style and comfort.",
    path: "/lakehills-car-service"
  }, {
    name: "Seguin",
    description: "Professional car service for Seguin area business and leisure travelers.",
    path: "/seguin-car-service"
  }, {
    name: "Wimberley",
    description: "Scenic Hill Country transportation for Wimberley's boutique shops, wineries, and natural attractions.",
    path: "/wimberley-car-service"
  }];
  return <>
      <Helmet>
        <title>Service Areas | Rick's Black Car Service San Antonio Coverage</title>
        <meta name="description" content="Rick's Black Car Service serves San Antonio, Boerne, New Braunfels, Stone Oak, Alamo Heights & all surrounding areas. Airport transfers, corporate & event transportation." />
        <link rel="canonical" href="https://ricksblackcarservicesantonio.com/service-areas" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Service Areas | Rick's Black Car Service" />
        <meta property="og:description" content="Rick's Black Car Service serves San Antonio, Boerne, New Braunfels, and all surrounding areas." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ricksblackcarservicesantonio.com/service-areas" />
      </Helmet>
      
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          {/* Hero Section */}
          <section className="relative py-20 overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{
            backgroundImage: `url(${hillCountryRivers})`
          }} />
            <div className="absolute inset-0 luxury-gradient opacity-60" />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center space-y-6">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground">
                  San Antonio Service Areas
                </h1>
                <p className="text-xl text-muted-foreground">
                  Rick's Black Car Service proudly provides professional transportation throughout San Antonio, Texas Hill Country, and surrounding communities.
                </p>
              </div>
            </div>
          </section>

          {/* Areas Grid */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
                {areas.map((area, index) => <Card key={index} className="card-luxury hover:scale-105 transition-all duration-300 group relative overflow-hidden">
                    {index === 0 && <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
                  backgroundImage: `url(${sanAntonioRiverwalk})`
                }} />}
                    {index === 1 && <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
                  backgroundImage: `url(${newBraunfels})`
                }} />}
                    {index === 2 && <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
                  backgroundImage: `url(${boerne})`
                }} />}
                    {index === 3 && <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
                  backgroundImage: `url(${canyonLake})`
                }} />}
                    {index === 4 && <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
                  backgroundImage: `url(${fairOaksRanch})`
                }} />}
                    {index === 5 && <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
                  backgroundImage: `url(${lakehills})`
                }} />}
                    {index === 6 && <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
                  backgroundImage: `url(${seguin})`
                }} />}
                    {index === 7 && <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
                  backgroundImage: `url(${hillCountryRivers})`
                }} />}
                    <CardContent className="p-8 space-y-4 relative z-10">
                      <MapPin className="w-10 h-10 text-primary group-hover:scale-110 transition-transform" />
                      <h3 className="text-2xl font-serif font-bold text-foreground">{area.name}</h3>
                      <p className="text-muted-foreground leading-relaxed">{area.description}</p>
                      <Button variant="ghost" className="text-primary hover:text-primary/80 p-0 group/button" asChild>
                        <Link to={area.path}>
                          Learn More <ArrowRight className="w-4 h-4 ml-1 inline-block group-hover/button:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>)}
              </div>
            </div>
          </section>

          {/* Detailed Service Areas */}
          <section className="py-20 bg-secondary">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto space-y-12">
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Core San Antonio</h2>
                      <p className="text-muted-foreground leading-relaxed">
                        Downtown San Antonio, Alamo Heights, Stone Oak, The Dominion, Medical Center, Terrell Hills, Olmos Park, Shavano Park, Hollywood Park, Balcones Heights, Castle Hills
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Greater San Antonio</h2>
                      <p className="text-muted-foreground leading-relaxed">
                        Boerne, New Braunfels, Schertz, Cibolo, Helotes, Leon Valley, Universal City, Converse, Live Oak, Selma, Garden Ridge, Fair Oaks Ranch
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Airports</h2>
                      <p className="text-muted-foreground leading-relaxed">
                        San Antonio International Airport (SAT), Austin-Bergstrom International Airport (AUS)
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Regional Destinations</h2>
                      <p className="text-muted-foreground leading-relaxed">
                        Texas Hill Country, Fredericksburg (wine country), Johnson City, Blanco, Comfort, Kerrville, Austin
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <Card className="card-luxury">
                  <CardContent className="p-8">
                    <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Neighborhoods We Serve</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Southtown Arts District, Pearl District, King William, Monte Vista, Mahncke Park, Tobin Hill, Beacon Hill, Dignowity Hill, Woodlawn Lake, Encino Park, Churchill Estates, Inwood, Northwood, Oak Park, Westover Hills, and all San Antonio neighborhoods.
                    </p>
                  </CardContent>
                </Card>

                <Card className="card-luxury">
                  <CardContent className="p-8">
                    <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Popular Destinations</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      River Walk, The Alamo, Pearl District, La Cantera, The Rim, AT&T Center, Alamodome, Tobin Center, San Antonio Zoo, SeaWorld, Six Flags Fiesta Texas
                    </p>
                  </CardContent>
                </Card>

                <div className="text-center pt-8">
                  <p className="text-xl text-muted-foreground mb-4">
                    Call (210) 296-7409 for service anywhere in San Antonio!
                  </p>
                </div>

              </div>
            </div>
          </section>


          {/* CTA Section */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center space-y-8">
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground">
                  Book Your Ride Today
                </h2>
                <p className="text-muted-foreground text-lg">
                  Experience premium transportation throughout the greater San Antonio area.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button className="btn-luxury text-lg px-8 py-6" asChild>
                    <a href="tel:+12102967409">
                      Call: 210-296-7409
                    </a>
                  </Button>
                  <Button variant="outline" className="text-lg px-8 py-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground" asChild>
                    
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Instant Quote CTA */}
          <InstantQuoteCTA />
        </main>
        <Footer />
      </div>
    </>;
};
export default ServiceAreas;